import type { FilterStates, FilterStatesKey } from '../type/filter-state';

/**
 * Get the number of applied filters for a specific filter key from the filter
 * states.
 * @param filterStates The filter states.
 * @param filterKey The filter key.
 * @returns The number of applied filters for the filter key.
 */
function getAppliedFilterCount(
    filterStates: FilterStates,
    filterKey: keyof FilterStates
): number {
    const filter = filterStates[filterKey];
    if (
        !filter ||
        typeof filter === 'number' ||
        typeof filter === 'string' ||
        Array.isArray(filter) ||
        !('kind' in filter)
    )
        return 0;
    switch (filter.kind) {
        case 'CategorySelector':
            return 0;
        case 'RangeSelector':
            return filter.value ? 1 : 0;
        case 'CheckboxGroup':
            return filter.checkedItems?.length ?? 0;
        case 'AwardsFilter':
            return filter.checkedItems?.length ?? 0;
        case 'SearchList':
            return filter.selectedItems?.length ?? 0;
        case 'Checkbox':
            return filter.checked ? 1 : 0;
        case 'RadioGroup':
            return filter.selectedItem && !filter.selectedItem.isDefault
                ? 1
                : 0;
        case 'Location':
            return filter.selectedLocation ? 1 : 0;
        default:
            console.error(
                `[filter-state-util] getAppliedFilterCount: unsupported filter kind: ${filter}`
            );
            return 0;
    }
}

/**
 * Check if there are any applied filters for the given filter keys.
 * @param filterStates The filter states.
 * @param filterKeys The filter keys to check.
 * @returns True if there are any applied filters, false otherwise.
 */
export function hasAppliedFilter(
    filterStates: FilterStates,
    filterKeys: FilterStatesKey[]
): boolean {
    return filterKeys.some(
        (filterKey) => getAppliedFilterCount(filterStates, filterKey) > 0
    );
}

/**
 * Get the number of applied filters from the filter states.
 * @param filterStates The filter states.
 * @param ignoreFilterKeys The filter keys to ignore.
 * @returns The number of applied filters.
 */
export function getAppliedFilterNumber(
    filterStates: FilterStates,
    ignoreFilterKeys: FilterStatesKey[] = []
): number {
    return Object.keys(filterStates).reduce((num, filterKey) => {
        if (ignoreFilterKeys.includes(filterKey)) {
            return num;
        }
        num += getAppliedFilterCount(
            filterStates,
            filterKey as keyof FilterStates
        );
        return num;
    }, 0);
}
